import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "../api";
import { getToken, saveSession } from "../auth";

const TREE_NODES = [
  { x: 200, y: 40, r: 14, root: true },
  { x: 110, y: 120, r: 10 },
  { x: 290, y: 120, r: 10 },
  { x: 60, y: 200, r: 7 },
  { x: 160, y: 200, r: 7 },
  { x: 240, y: 200, r: 7 },
  { x: 340, y: 200, r: 7 },
  { x: 35, y: 268, r: 4.5 },
  { x: 85, y: 268, r: 4.5 },
  { x: 135, y: 268, r: 4.5 },
  { x: 185, y: 268, r: 4.5 },
  { x: 215, y: 268, r: 4.5 },
  { x: 265, y: 268, r: 4.5 },
  { x: 315, y: 268, r: 4.5 },
  { x: 365, y: 268, r: 4.5 },
];

const TREE_LINKS = [
  [0, 1], [0, 2], [1, 3], [1, 4], [2, 5], [2, 6],
  [3, 7], [3, 8], [4, 9], [4, 10], [5, 11], [5, 12], [6, 13], [6, 14],
];

const FEATURES = [
  { title: "Network control", text: "Members, genealogy and package activations in one place." },
  { title: "Payout oversight", text: "Review deposits, withdrawals and reward claims before they settle." },
  { title: "Audited access", text: "Admin-only sessions, separate from member accounts." },
];

function NetworkTree() {
  return (
    <svg className="login-tree" viewBox="0 0 400 300" aria-hidden="true">
      <defs>
        <linearGradient id="treeLine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#d9b46a" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#b88a45" stopOpacity="0.25" />
        </linearGradient>
        <radialGradient id="treeRoot">
          <stop offset="0%" stopColor="#f3d79b" />
          <stop offset="100%" stopColor="#c08a2e" />
        </radialGradient>
      </defs>
      {TREE_LINKS.map(([a, b], index) => {
        const from = TREE_NODES[a];
        const to = TREE_NODES[b];
        const midY = (from.y + to.y) / 2;
        return (
          <path
            key={index}
            className="tree-link"
            style={{ animationDelay: `${index * 0.18}s` }}
            d={`M${from.x} ${from.y} C${from.x} ${midY}, ${to.x} ${midY}, ${to.x} ${to.y}`}
          />
        );
      })}
      {TREE_NODES.map((node, index) => (
        <g key={index} className="tree-node" style={{ animationDelay: `${0.3 + index * 0.08}s` }}>
          {node.root ? <circle cx={node.x} cy={node.y} r={node.r + 10} className="tree-halo" /> : null}
          <circle
            cx={node.x}
            cy={node.y}
            r={node.r}
            fill={node.root ? "url(#treeRoot)" : "#0f1219"}
            stroke={node.root ? "none" : "#e3bf7a"}
            strokeWidth="1.5"
          />
        </g>
      ))}
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="m4 7 8 6 8-6" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

function EyeIcon({ off }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
      {off ? <path d="m4 4 16 16" /> : null}
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3 4.5 6v5.5c0 4.6 3.2 8.4 7.5 9.5 4.3-1.1 7.5-4.9 7.5-9.5V6L12 3Z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function CodeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="5" width="16" height="14" rx="2.5" />
      <path d="M8 12h.01M12 12h.01M16 12h.01" />
    </svg>
  );
}

function failureState(err) {
  const message = err.message || "Request failed";
  const fieldErrors = err.errors || {};
  if (Object.keys(fieldErrors).length) {
    return { error: message === "Validation failed" ? "" : message, errors: fieldErrors };
  }
  if (/otp/i.test(message)) return { error: "", errors: { otp: message } };
  return { error: message, errors: {} };
}

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState("");
  const [resendIn, setResendIn] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!challenge || resendIn <= 0) return undefined;
    const timer = window.setTimeout(() => setResendIn((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [challenge, resendIn]);

  if (getToken()) return <Navigate to="/admin" replace />;

  function applyFailure(err) {
    const next = failureState(err);
    setError(next.error);
    setErrors(next.errors);
  }

  function beginChallenge(data) {
    setChallenge({
      challengeId: data.challengeId,
      sentTo: data.sentTo || "",
      expiresInSeconds: data.expiresInSeconds || 600,
      otp: data.otp || "",
    });
    setOtp("");
    setResendIn(30);
    setError("");
    setErrors({});
  }

  async function onSubmit(event) {
    event.preventDefault();
    if (challenge) {
      await verifyOtp();
      return;
    }

    const nextErrors = {};
    if (!email.trim()) nextErrors.email = "Enter a valid email";
    if (!password) nextErrors.password = "Password is required";
    setErrors(nextErrors);
    setError("");
    if (Object.keys(nextErrors).length) return;

    setBusy(true);
    try {
      const result = await api("/auth/login", {
        method: "POST",
        auth: false,
        body: { email: email.trim(), password },
      });
      beginChallenge(result.data || {});
    } catch (err) {
      applyFailure(err);
    } finally {
      setBusy(false);
    }
  }

  async function verifyOtp() {
    const code = otp.trim();
    if (!/^\d{6}$/.test(code)) {
      setErrors({ otp: "Enter the 6 digit OTP" });
      setError("");
      return;
    }

    setBusy(true);
    setError("");
    setErrors({});
    try {
      const result = await api("/auth/login/verify", {
        method: "POST",
        auth: false,
        body: { challengeId: challenge.challengeId, otp: code },
      });
      saveSession(result.data.token, result.data.admin);
      navigate("/admin");
    } catch (err) {
      applyFailure(err);
    } finally {
      setBusy(false);
    }
  }

  async function resendOtp() {
    if (resendIn > 0 || busy) return;
    setBusy(true);
    setError("");
    setErrors({});
    try {
      const result = await api("/auth/otp/resend", {
        method: "POST",
        auth: false,
        body: { challengeId: challenge.challengeId },
      });
      beginChallenge(result.data || challenge);
    } catch (err) {
      applyFailure(err);
    } finally {
      setBusy(false);
    }
  }

  function useDifferentAccount() {
    setChallenge(null);
    setOtp("");
    setResendIn(0);
    setError("");
    setErrors({});
  }

  return (
    <div className="login-screen">
      <aside className="login-hero">
        <div className="login-hero-glow" />
        <div className="brand brand-lockup">
          <img className="brand-logo" src="/finitygrowth-logo.svg" alt="Finitygrowth" />
          <em>Network Administration</em>
        </div>

        <div className="login-hero-body">
          <p className="login-eyebrow">Control center</p>
          <h2>
            Every level of your network, <span>in one view.</span>
          </h2>
          <NetworkTree />
          <ul className="login-features">
            {FEATURES.map((feature) => (
              <li key={feature.title}>
                <span className="login-feature-dot" />
                <div>
                  <strong>{feature.title}</strong>
                  <p>{feature.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <p className="login-hero-foot">© {new Date().getFullYear()} Finitygrowth. All rights reserved.</p>
      </aside>

      <main className="login-panel">
        <form className="login-card" onSubmit={onSubmit} noValidate>
          <div className="brand brand-lockup login-card-brand">
            <img className="brand-logo" src="/finitygrowth-logo.svg" alt="Finitygrowth" />
            <em>Admin</em>
          </div>

          <span className="login-badge">
            <ShieldIcon />
            Secure admin access
          </span>
          <h1>{challenge ? "Enter the code" : "Welcome back"}</h1>
          <p className="lead">
            {challenge
              ? `A 6 digit code was sent to ${challenge.sentTo || "your admin email"}. It expires in ${Math.round((challenge.expiresInSeconds || 600) / 60)} minutes.`
              : "Sign in with the email and password saved on your admin account. Member credentials are not accepted here."}
          </p>

          {error ? <div className="alert login-alert">{error}</div> : null}

          <div className="stack">
            <label className={errors.email ? "login-field has-error" : "login-field"}>
              <span>Email address</span>
              <div className="login-input">
                <MailIcon />
                <input
                  type="email"
                  placeholder="name@finitygrowth.com"
                  autoComplete="username"
                  value={email}
                  disabled={Boolean(challenge)}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              {errors.email ? <small className="field-error">{errors.email}</small> : null}
            </label>

            <label className={errors.password ? "login-field has-error" : "login-field"}>
              <span>Password</span>
              <div className="login-input">
                <LockIcon />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  value={password}
                  disabled={Boolean(challenge)}
                  onChange={(event) => setPassword(event.target.value)}
                />
                <button
                  type="button"
                  className="login-eye"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  disabled={Boolean(challenge)}
                >
                  <EyeIcon off={showPassword} />
                </button>
              </div>
              {errors.password ? <small className="field-error">{errors.password}</small> : null}
            </label>

            {challenge ? (
              <label className={errors.otp ? "login-field has-error" : "login-field"}>
                <span>OTP</span>
                <div className="login-input">
                  <CodeIcon />
                  <input
                    className="login-otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="6 digit code"
                    maxLength={6}
                    value={otp}
                    onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  />
                </div>
                {errors.otp ? <small className="field-error">{errors.otp}</small> : null}
              </label>
            ) : null}

            <button className="btn login-submit" disabled={busy}>
              {busy ? <span className="login-spinner" /> : null}
              {busy ? "Please wait..." : challenge ? "Verify and sign in" : "Sign in to dashboard"}
            </button>

            {challenge ? (
              <div className="login-otp-actions">
                <button type="button" className="login-text-btn" disabled={busy || resendIn > 0} onClick={resendOtp}>
                  {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
                </button>
                <button type="button" className="login-text-btn" disabled={busy} onClick={useDifferentAccount}>
                  Use a different account
                </button>
              </div>
            ) : null}
          </div>

        </form>
      </main>
    </div>
  );
}
